import React, { useEffect, useState, useCallback, useRef } from 'react';
import { grafikService } from '../../../services/grafikService';
import { parseShiftTime } from '../../../utils/grafikHelpers';

/**
 * Live status panel: kto teraz w pracy / spozniony / nie pojawil sie / zakonczyl.
 * Auto-refresh co 60s. Filtrowany po locationId (jezeli podane).
 *
 * Props:
 *   locationId: string|number|undefined  - filtr lokalizacji
 *   refreshIntervalMs: number (default 60000)
 */
const REFRESH_MS_DEFAULT = 60000;

const STATUS_META = {
  working: { icon: 'fa-check-circle', color: 'var(--success)', label: 'W pracy' },
  late_no_show: { icon: 'fa-times-circle', color: 'var(--danger)', label: 'Nie pojawil sie' },
  planned_yet: { icon: 'fa-clock', color: 'var(--text-muted)', label: 'Czeka' },
  done: { icon: 'fa-flag-checkered', color: 'var(--info)', label: 'Zakonczona' }
};

const LiveStatusPanel = ({ locationId, refreshIntervalMs = REFRESH_MS_DEFAULT }) => {
  const [shifts, setShifts] = useState([]);
  const [summary, setSummary] = useState({ planned: 0, working: 0, late: 0, done: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const intervalRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const res = await grafikService.getLiveStatus(locationId || undefined);
      const data = res?.data || res || {};
      const arr = data?.shifts || res?.shifts || [];
      setShifts(Array.isArray(arr) ? arr : []);
      setSummary(data?.summary || res?.summary || { planned: 0, working: 0, late: 0, done: 0 });
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.message || 'Blad ladowania live status');
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    setLoading(true);
    load();
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => { load(); }, refreshIntervalMs);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, refreshIntervalMs]);

  const totalPlanned = summary?.planned || shifts.length || 0;
  const lateCount = summary?.late || shifts.filter(s => s.spoznienie_minut > 0 && s.status === 'working').length;
  const lateMinutesSum = shifts
    .filter(s => s.status === 'working' && s.spoznienie_minut > 0)
    .reduce((sum, s) => sum + Number(s.spoznienie_minut || 0), 0);

  const headerColor = lateCount > 0 ? 'var(--warning)' : (totalPlanned > 0 ? 'var(--success)' : 'var(--text-muted)');

  const renderRow = (s) => {
    const meta = STATUS_META[s.status] || STATUS_META.planned_yet;
    const isLate = s.status === 'working' && Number(s.spoznienie_minut || 0) > 0;
    const isNoShow = s.status === 'late_no_show';
    const rowColor = isNoShow ? 'var(--danger)'
      : isLate ? 'var(--warning)'
        : meta.color;

    let suffix = '';
    if (s.status === 'working') {
      suffix = isLate
        ? `Spoznil sie ${s.spoznienie_minut} min`
        : 'Punktualny';
    } else if (s.status === 'planned_yet') {
      suffix = 'Czeka (jeszcze nie)';
    } else if (s.status === 'late_no_show') {
      suffix = `Nie pojawil sie ${s.spoznienie_minut || ''} min`.trim();
    } else if (s.status === 'done') {
      suffix = 'Zakonczona';
    }

    const startStr = s.czas_rozpoczecia ? parseShiftTime(s.czas_rozpoczecia) : '—';

    return (
      <div
        key={s.id}
        style={{
          display: 'grid',
          gridTemplateColumns: '160px 1fr 110px 110px 1fr',
          gap: '0.5rem',
          alignItems: 'center',
          padding: '0.4rem 0.6rem',
          borderBottom: '1px solid var(--border)',
          fontSize: '0.85rem'
        }}
      >
        <div style={{ fontWeight: 600, color: 'var(--text)' }}>{s.user_login}</div>
        <div style={{ color: 'var(--text-muted)' }}>{s.location_nazwa || '—'}</div>
        <div style={{ color: 'var(--text)' }}>
          {parseShiftTime(s.godzina_od)}–{parseShiftTime(s.godzina_do)}
        </div>
        <div style={{ color: 'var(--text)' }}>{startStr}</div>
        <div style={{ color: rowColor, fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <i className={`fas ${meta.icon}`} />
          {suffix}
        </div>
      </div>
    );
  };

  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: '6px',
      backgroundColor: 'var(--card-bg)',
      marginBottom: '1rem',
      overflow: 'hidden'
    }}>
      {/* Naglowek */}
      <div
        style={{
          padding: '0.6rem 1rem',
          backgroundColor: 'var(--bg)',
          borderBottom: collapsed ? 'none' : '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.6rem',
          cursor: 'pointer'
        }}
        onClick={() => setCollapsed(c => !c)}
      >
        <span style={{
          width: 10, height: 10, borderRadius: '50%',
          backgroundColor: headerColor, display: 'inline-block'
        }} />
        <strong style={{ fontSize: '0.9rem', color: 'var(--text)' }}>
          Live status:
        </strong>
        <span style={{ fontSize: '0.85rem', color: 'var(--text)' }}>
          Teraz w pracy: {summary?.working || 0}/{totalPlanned}
          {lateCount > 0 && (
            <span style={{ color: 'var(--warning)', marginLeft: '0.5rem' }}>
              ({lateCount} spoznionych, lacznie {lateMinutesSum} min)
            </span>
          )}
          {summary?.done > 0 && (
            <span style={{ color: 'var(--text-muted)', marginLeft: '0.5rem' }}>
              | Zakonczone: {summary.done}
            </span>
          )}
        </span>
        <div style={{ flex: 1 }} />
        {lastRefresh && (
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            ostatnia aktualizacja: {lastRefresh.toLocaleTimeString('pl-PL')}
          </span>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); load(); }}
          disabled={loading}
          style={{
            padding: '4px 10px', fontSize: '11px',
            background: 'transparent', color: 'var(--info)',
            border: '1px solid var(--info)', cursor: 'pointer'
          }}
          title="Odswiez teraz"
        >
          <i className={`fas fa-sync ${loading ? 'fa-spin' : ''}`} style={{ marginRight: 4 }} />
          Odswiez
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setCollapsed(c => !c); }}
          style={{
            padding: '4px 10px', fontSize: '11px',
            background: 'transparent', color: 'var(--text-muted)',
            border: 'none', cursor: 'pointer'
          }}
          title={collapsed ? 'Rozwin' : 'Zwin'}
        >
          <i className={`fas ${collapsed ? 'fa-chevron-down' : 'fa-chevron-up'}`} />
        </button>
      </div>

      {/* Cialo */}
      {!collapsed && (
        <div>
          {loading && shifts.length === 0 ? (
            <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              <div className="spinner-border spinner-border-sm" style={{ marginRight: '0.4rem' }} />
              Ladowanie live status...
            </div>
          ) : error ? (
            <div style={{ padding: '0.8rem 1rem', color: 'var(--danger)', fontSize: '0.85rem' }}>
              {error}
            </div>
          ) : shifts.length === 0 ? (
            <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              Brak zaplanowanych zmian na dzis.
            </div>
          ) : (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '160px 1fr 110px 110px 1fr',
                gap: '0.5rem',
                padding: '0.4rem 0.6rem',
                fontSize: '0.7rem',
                color: 'var(--text-muted)',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
                borderBottom: '1px solid var(--border)',
                backgroundColor: 'var(--bg)'
              }}>
                <div>Pracownik</div>
                <div>Lokalizacja</div>
                <div>Plan</div>
                <div>Start</div>
                <div>Status</div>
              </div>
              {shifts.map(renderRow)}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default LiveStatusPanel;
